import { formatEurPerKwh } from '../utils/financialCalculations';
import type { FinancialKpis } from '../utils/financialCalculations';

const C = { pri: '#0B1F4A', sec: '#00A3C4', grn: '#10B981', org: '#F59E0B', rd: '#EF4444' };

function FKpi({ icon, label, value, sub, color }: { icon: string; label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="bg-white rounded-xl px-4 py-3.5 border border-[#D9E2EC] shadow-sm flex flex-col gap-1" style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-1.5">
        <span className="text-base">{icon}</span>
        <span className="text-[10px] font-semibold text-[#8899AA] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-xl font-extrabold text-[#0B1F4A] tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-[#8899AA] leading-snug">{sub}</div>}
    </div>
  );
}

function fmtNL(v: number, decimals = 0): string { return v.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }); }
function fmt(v: number, prefix = '', suffix = ''): string {
  if (Math.abs(v) >= 1e6) return `${prefix}${fmtNL(v / 1e6, 1)}M${suffix}`;
  if (Math.abs(v) >= 1e3) return `${prefix}${fmtNL(Math.round(v / 1e3))}k${suffix}`;
  return `${prefix}${fmtNL(v, 1)}${suffix}`;
}

export default function FinancialKpiCards({ kpis }: { kpis: FinancialKpis }) {
  const npvColor = kpis.npv >= 0 ? C.grn : C.rd;
  const irrColor = kpis.irr >= 8 ? C.grn : kpis.irr >= 5 ? C.org : C.rd;
  const paybackColor = kpis.paybackYear !== null && kpis.paybackYear <= 10 ? C.grn : kpis.paybackYear !== null && kpis.paybackYear <= 15 ? C.org : C.rd;
  const dscrColor = kpis.minDscr >= 1.3 ? C.grn : kpis.minDscr >= 1.1 ? C.org : C.rd;

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2.5 mb-4">
      <FKpi icon="💰" label="NPV (na belasting)" value={fmt(kpis.npvAfterTax, '€')} color={npvColor} sub={kpis.npvAfterTax >= 0 ? 'Na VPB, incl. EIA/MIA' : 'Negatieve netto contante waarde'} />
      <FKpi icon="📈" label="Project IRR" value={`${isNaN(kpis.irrAfterTax) ? '—' : kpis.irrAfterTax.toFixed(1).replace('.', ',')}%`} color={irrColor} sub="IRR na belasting" />
      <FKpi icon="🎯" label="Equity IRR" value={`${isNaN(kpis.equityIrr) ? '—' : kpis.equityIrr.toFixed(1).replace('.', ',')}%`} color={kpis.equityIrr >= 12 ? C.grn : kpis.equityIrr >= 8 ? C.org : C.rd} sub="Rendement op eigen vermogen" />
      <FKpi icon="⏱️" label="Terugverdientijd" value={kpis.paybackYear !== null ? `${kpis.paybackYear.toFixed(1).replace('.', ',')} jaar` : '> levensduur'} color={paybackColor} sub={kpis.paybackYear !== null ? 'Eenvoudige terugverdientijd' : 'Investering niet terugverdiend'} />
      {/* LCOE en marge kunnen niet-eindig zijn: bij nul geleverde energie zijn de
          kosten niet per kWh uit te drukken. Op de Wognum-invoer met pv 0, gMax 0,
          bP 0, buP 0 en 19.000 MWh vraag (100 % ongedekt, NPV −€14.001.448) gaf de
          oude weergave "€0,000/kWh" LCOE en "€0,300/kWh" marge — winst uit stroom
          die nooit geleverd werd. `formatEurPerKwh` maakt daar "n.v.t." van. */}
      <FKpi icon="⚡" label="LCOE" value={formatEurPerKwh(kpis.lcoe)} color={C.sec}
        sub={Number.isFinite(kpis.lcoe) ? 'Levelized Cost of Energy (verdisconteerd)' : 'Geen geleverde energie — kosten niet per kWh te verdelen'} />
      <FKpi icon="📊" label="Marge/kWh" value={formatEurPerKwh(kpis.marginPerKwh)} color={kpis.marginPerKwh >= 0 ? C.grn : C.rd}
        sub={Number.isFinite(kpis.marginPerKwh) ? 'Laadtarief − LCOE' : 'Geen marge zonder levering'} />
      <FKpi icon="🔄" label="ROI" value={`${fmtNL(Math.round(kpis.roi))}%`} color={kpis.roi >= 100 ? C.grn : kpis.roi >= 50 ? C.org : C.rd} sub="Return on Investment" />
      <FKpi icon="🏦" label="Min DSCR" value={`${kpis.minDscr.toFixed(2).replace('.', ',')}x`} color={dscrColor} sub={`Gem. ${kpis.avgDscr.toFixed(2).replace('.', ',')}x | Schuld €${fmt(kpis.debtAmount)}`} />
      <FKpi icon="🌱" label="CO₂ vermeden" value={`${fmtNL(Math.round(kpis.totalCo2Avoided))} ton`} color={C.grn} sub={`ERE-opbrengst €${fmt(kpis.co2CertificateValue)}`} />
    </div>
  );
}
